import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, CheckCircle2, FileText, Building2, MapPin, CreditCard, ShieldCheck } from 'lucide-react';
import { COUNTRIES, countryByCode, dialCodes } from '../lib/marketplace-geo';
import { DateInput } from '@/components/ui/date-input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Public onboarding form for the marketplace vendor portal.
// Anyone with the link `/marketplace/onboarding/:companyId` can apply.
// On submit the vendor row is created with status="pending_approval".
const MarketplaceOnboardingPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [pageSettings, setPageSettings] = useState(null);
  const [pageError, setPageError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedVendorId, setSubmittedVendorId] = useState(null);

  const [form, setForm] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    country: 'United Arab Emirates',
    city: '',
    phone_dial: '+971',
    phone_number: '',
    pickup_address: {
      warehouse_name: '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      pincode: '',
      country_code: 'AE',
      country: 'United Arab Emirates',
    },
    trade_license_number: '',
    trade_license_expiry: '',
    trade_license_doc_base64: '',
    tax_certificate_number: '',
    tax_certificate_expiry: '',
    tax_certificate_doc_base64: '',
    bank_details_struct: {
      bank_name: '',
      account_holder: '',
      account_number: '',
      country_code: 'AE',
      iban: '',
      swift: '',
      ifsc: '',
      routing_number: '',
      sort_code: '',
      branch: '',
    },
    terms_accepted: false,
    portal_username: '',
    portal_password: '',
  });

  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/marketplace/onboarding/${companyId}/page-settings`)
      .then((r) => setPageSettings(r.data))
      .catch((e) => setPageError(e.response?.data?.detail || 'This onboarding link is invalid.'));
  }, [companyId]);

  const updateField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleFileUpload = (key) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error('File must be under 4 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, [key]: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    // Validate top-level required fields (phone is constructed below)
    for (const req of [
      'company_name', 'contact_person', 'email',
      'trade_license_number', 'trade_license_expiry',
      'trade_license_doc_base64', 'portal_username', 'portal_password',
    ]) {
      if (!form[req] || !String(form[req]).trim()) {
        toast.error(`Please fill: ${req.replaceAll('_', ' ')}`);
        return;
      }
    }
    if (!form.phone_number || form.phone_number.length < 6) {
      toast.error('Please enter a valid phone number');
      return;
    }
    // Validate structured pickup address
    for (const k of ['warehouse_name', 'line1', 'city', 'pincode', 'country']) {
      if (!form.pickup_address[k] || !String(form.pickup_address[k]).trim()) {
        toast.error(`Please fill pickup ${k.replace(/_/g, ' ')}`);
        return;
      }
    }
    if (form.portal_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    // Require T&C acceptance only when admin has configured them
    if (pageSettings?.terms?.content && !form.terms_accepted) {
      toast.error('Please accept the terms & conditions to continue');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        // Combine dial code + national number into E.164 (e.g. "+971501234567")
        phone: `${form.phone_dial || '+971'}${(form.phone_number || '').replace(/[^0-9]/g, '')}`,
        terms_version: pageSettings?.terms?.version || '',
      };
      const r = await axios.post(`${API}/marketplace/onboarding/${companyId}`, payload);
      setSubmittedVendorId(r.data.vendor_id);
      toast.success('Application submitted! Awaiting admin approval.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (pageError) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8">
        <div className="max-w-md text-center text-slate-300">
          <h1 className="text-2xl font-semibold mb-3">Onboarding unavailable</h1>
          <p className="text-sm text-slate-500">{pageError}</p>
        </div>
      </div>
    );
  }
  if (!pageSettings) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }
  if (submittedVendorId) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8">
        <div className="max-w-lg text-center" data-testid="onboarding-success">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/15 mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-semibold text-slate-100 mb-2">Application Submitted</h1>
          <p className="text-slate-400 mb-1">{pageSettings.company_name} will review your documents.</p>
          <p className="text-slate-500 text-sm mb-6">You&apos;ll get an email once you&apos;re approved. Your application ID is:</p>
          <code className="px-3 py-2 rounded bg-slate-900 border border-slate-800 text-slate-200 font-mono text-sm">
            {submittedVendorId}
          </code>
          <div className="mt-8">
            <button
              onClick={() => navigate('/marketplace-vendor/login')}
              className="text-sm text-amber-400 hover:underline"
              data-testid="onboarding-login-link"
            >
              Already approved? Sign in →
            </button>
          </div>
        </div>
      </div>
    );
  }

  const primary = pageSettings.primary_color || '#D4AF37';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-6">
          {pageSettings.logo_url
            ? <img src={pageSettings.logo_url} alt="logo" className="h-10 w-auto" />
            : <Building2 className="w-9 h-9" style={{ color: primary }} />}
          <div>
            <h1 className="text-3xl font-semibold" style={{ color: primary }}>{pageSettings.headline}</h1>
            <p className="text-slate-400 text-sm mt-0.5">{pageSettings.sub_headline}</p>
          </div>
        </div>
        {pageSettings.accept_blurb && (
          <p className="text-sm text-slate-400 mb-6 leading-relaxed">{pageSettings.accept_blurb}</p>
        )}

        <div className="space-y-6" data-testid="marketplace-onboarding-form">
          <Section title="Company Details" icon={<Building2 className="w-4 h-4" />}>
            <Field label="Legal Company Name *" testid="onb-company-name">
              <input value={form.company_name} onChange={updateField('company_name')} className={cls()} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Contact Person *">
                <input value={form.contact_person} onChange={updateField('contact_person')} className={cls()} />
              </Field>
              <Field label="Email *">
                <input type="email" value={form.email} onChange={updateField('email')} className={cls()} />
              </Field>
              <Field label="Phone *">
                <div className="flex gap-2 items-stretch">
                  <select
                    value={form.phone_dial || '+971'}
                    onChange={(e) => setForm((f) => ({ ...f, phone_dial: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 text-sm focus:outline-none focus:border-amber-400 w-32 shrink-0"
                    data-testid="onb-phone-dial"
                  >
                    {dialCodes.map((d) => (
                      <option key={d.code} value={d.dial}>{d.dial} ({d.code})</option>
                    ))}
                  </select>
                  <input
                    value={form.phone_number || ''}
                    onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value.replace(/[^0-9]/g, '') }))}
                    placeholder="50 123 4567"
                    inputMode="numeric"
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 text-sm focus:outline-none focus:border-amber-400"
                    data-testid="onb-phone-number"
                  />
                </div>
              </Field>
              <Field label="Country">
                <select
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  className={cls()}
                  data-testid="onb-company-country"
                >
                  {COUNTRIES.filter((c) => c.code !== 'OTHER').map((c) => (
                    <option key={c.code} value={c.name}>{c.name}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </Field>
              <Field label="City">
                <input value={form.city} onChange={updateField('city')} className={cls()} />
              </Field>
            </div>
          </Section>

          <Section title="Pickup Address" icon={<MapPin className="w-4 h-4" />}>
            <p className="text-xs text-slate-500 mb-3">
              Used as the courier pickup location. All fields are required so we can register your
              warehouse with the courier partner during fulfillment.
            </p>
            <Field label="Warehouse / Office Name *">
              <input
                value={form.pickup_address.warehouse_name}
                onChange={(e) => setForm((f) => ({ ...f, pickup_address: { ...f.pickup_address, warehouse_name: e.target.value } }))}
                className={cls()}
                data-testid="onb-pickup-warehouse-name"
                placeholder="e.g. Dubai Main Warehouse"
              />
            </Field>
            <Field label="Address Line 1 *">
              <input
                value={form.pickup_address.line1}
                onChange={(e) => setForm((f) => ({ ...f, pickup_address: { ...f.pickup_address, line1: e.target.value } }))}
                className={cls()}
                data-testid="onb-pickup-line1"
                placeholder="Street, building, unit"
              />
            </Field>
            <Field label="Address Line 2">
              <input
                value={form.pickup_address.line2}
                onChange={(e) => setForm((f) => ({ ...f, pickup_address: { ...f.pickup_address, line2: e.target.value } }))}
                className={cls()}
                data-testid="onb-pickup-line2"
                placeholder="Area / landmark (optional)"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Country *">
                <select
                  value={form.pickup_address.country_code || 'AE'}
                  onChange={(e) => {
                    const cc = e.target.value;
                    const c = countryByCode(cc);
                    setForm((f) => ({
                      ...f,
                      pickup_address: {
                        ...f.pickup_address,
                        country_code: cc,
                        country: c.name,
                        state: '', // reset state when country changes
                      },
                    }));
                  }}
                  className={cls()}
                  data-testid="onb-pickup-country"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </Field>
              <Field label={(form.pickup_address.country_code === 'AE') ? 'Emirate *' : 'State *'}>
                {(() => {
                  const states = countryByCode(form.pickup_address.country_code || 'AE').states;
                  if (states && states.length) {
                    return (
                      <select
                        value={form.pickup_address.state}
                        onChange={(e) => setForm((f) => ({ ...f, pickup_address: { ...f.pickup_address, state: e.target.value } }))}
                        className={cls()}
                        data-testid="onb-pickup-state"
                      >
                        <option value="">Select…</option>
                        {states.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    );
                  }
                  return (
                    <input
                      value={form.pickup_address.state}
                      onChange={(e) => setForm((f) => ({ ...f, pickup_address: { ...f.pickup_address, state: e.target.value } }))}
                      className={cls()}
                      data-testid="onb-pickup-state"
                      placeholder="State / Province"
                    />
                  );
                })()}
              </Field>
              <Field label="City *">
                <input
                  value={form.pickup_address.city}
                  onChange={(e) => setForm((f) => ({ ...f, pickup_address: { ...f.pickup_address, city: e.target.value } }))}
                  className={cls()}
                  data-testid="onb-pickup-city"
                />
              </Field>
              <Field label="Pincode / Postal Code *">
                <input
                  value={form.pickup_address.pincode}
                  onChange={(e) => setForm((f) => ({ ...f, pickup_address: { ...f.pickup_address, pincode: e.target.value } }))}
                  className={cls()}
                  data-testid="onb-pickup-pincode"
                />
              </Field>
            </div>
          </Section>

          <Section title="Compliance Documents" icon={<ShieldCheck className="w-4 h-4" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Trade License # *">
                <input value={form.trade_license_number} onChange={updateField('trade_license_number')} className={cls()} />
              </Field>
              <Field label="Trade License Expiry *">
                <DateInput  value={form.trade_license_expiry} onChange={updateField('trade_license_expiry')} className={cls()} />
              </Field>
            </div>
            <FileUpload
              label="Upload Trade License (PDF/JPG/PNG, max 4 MB) *"
              filled={!!form.trade_license_doc_base64}
              onChange={handleFileUpload('trade_license_doc_base64')}
              testid="onb-trade-license-upload"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <Field label="Tax Certificate #">
                <input value={form.tax_certificate_number} onChange={updateField('tax_certificate_number')} className={cls()} />
              </Field>
              <Field label="Tax Certificate Expiry">
                <DateInput  value={form.tax_certificate_expiry} onChange={updateField('tax_certificate_expiry')} className={cls()} />
              </Field>
            </div>
            <FileUpload
              label="Upload Tax Certificate (optional)"
              filled={!!form.tax_certificate_doc_base64}
              onChange={handleFileUpload('tax_certificate_doc_base64')}
              testid="onb-tax-cert-upload"
            />
          </Section>

          <Section title="Bank Details (for payouts)" icon={<CreditCard className="w-4 h-4" />}>
            <Field label="Bank Country *">
              <select
                value={form.bank_details_struct.country_code}
                onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, country_code: e.target.value } }))}
                className={cls()}
                data-testid="onb-bank-country"
              >
                <option value="AE">United Arab Emirates (IBAN + SWIFT)</option>
                <option value="IN">India (IFSC)</option>
                <option value="US">United States (Routing Number)</option>
                <option value="GB">United Kingdom (Sort Code)</option>
                <option value="SA">Saudi Arabia (IBAN + SWIFT)</option>
                <option value="OM">Oman (IBAN + SWIFT)</option>
                <option value="OTHER">Other (SWIFT only)</option>
              </select>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Bank Name">
                <input
                  value={form.bank_details_struct.bank_name}
                  onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, bank_name: e.target.value } }))}
                  className={cls()}
                  data-testid="onb-bank-name"
                />
              </Field>
              <Field label="Account Holder Name">
                <input
                  value={form.bank_details_struct.account_holder}
                  onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, account_holder: e.target.value } }))}
                  className={cls()}
                  data-testid="onb-bank-holder"
                />
              </Field>
              <Field label="Account Number">
                <input
                  value={form.bank_details_struct.account_number}
                  onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, account_number: e.target.value } }))}
                  className={cls()}
                  data-testid="onb-bank-account"
                />
              </Field>
              <Field label="Branch (optional)">
                <input
                  value={form.bank_details_struct.branch}
                  onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, branch: e.target.value } }))}
                  className={cls()}
                />
              </Field>

              {/* Country-specific fields */}
              {['AE', 'SA', 'OM'].includes(form.bank_details_struct.country_code) && (
                <>
                  <Field label="IBAN">
                    <input
                      value={form.bank_details_struct.iban}
                      onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, iban: e.target.value.toUpperCase() } }))}
                      className={cls()}
                      data-testid="onb-bank-iban"
                    />
                  </Field>
                  <Field label="SWIFT / BIC">
                    <input
                      value={form.bank_details_struct.swift}
                      onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, swift: e.target.value.toUpperCase() } }))}
                      className={cls()}
                      data-testid="onb-bank-swift"
                    />
                  </Field>
                </>
              )}
              {form.bank_details_struct.country_code === 'IN' && (
                <Field label="IFSC Code">
                  <input
                    value={form.bank_details_struct.ifsc}
                    onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, ifsc: e.target.value.toUpperCase() } }))}
                    className={cls()}
                    data-testid="onb-bank-ifsc"
                    placeholder="e.g. HDFC0000123"
                    maxLength={11}
                  />
                </Field>
              )}
              {form.bank_details_struct.country_code === 'US' && (
                <Field label="Routing Number (ABA)">
                  <input
                    value={form.bank_details_struct.routing_number}
                    onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, routing_number: e.target.value } }))}
                    className={cls()}
                    data-testid="onb-bank-routing"
                    maxLength={9}
                  />
                </Field>
              )}
              {form.bank_details_struct.country_code === 'GB' && (
                <Field label="Sort Code">
                  <input
                    value={form.bank_details_struct.sort_code}
                    onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, sort_code: e.target.value } }))}
                    className={cls()}
                    data-testid="onb-bank-sortcode"
                    placeholder="e.g. 12-34-56"
                  />
                </Field>
              )}
              {form.bank_details_struct.country_code === 'OTHER' && (
                <Field label="SWIFT / BIC">
                  <input
                    value={form.bank_details_struct.swift}
                    onChange={(e) => setForm((f) => ({ ...f, bank_details_struct: { ...f.bank_details_struct, swift: e.target.value.toUpperCase() } }))}
                    className={cls()}
                    data-testid="onb-bank-swift"
                  />
                </Field>
              )}
            </div>
          </Section>

          <Section title="Create Your Login" icon={<FileText className="w-4 h-4" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Username * (lowercase, 3-40 chars)">
                <input
                  value={form.portal_username}
                  onChange={(e) => setForm((f) => ({ ...f, portal_username: e.target.value.toLowerCase() }))}
                  className={cls()}
                  data-testid="onb-username"
                />
              </Field>
              <Field label="Password * (min 8 chars)">
                <input type="password" value={form.portal_password} onChange={updateField('portal_password')} className={cls()} data-testid="onb-password" />
              </Field>
            </div>
          </Section>

          {/* Terms & Conditions */}
          {pageSettings?.terms?.content && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="onb-terms-section">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.terms_accepted}
                  onChange={(e) => setForm((f) => ({ ...f, terms_accepted: e.target.checked }))}
                  className="mt-1 w-4 h-4 rounded border-slate-600"
                  data-testid="onb-terms-checkbox"
                />
                <span className="text-sm text-slate-300">
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setTermsOpen(true)}
                    className="underline hover:text-amber-300"
                    style={{ color: primary }}
                    data-testid="onb-terms-link"
                  >
                    Terms &amp; Conditions
                  </button>
                  {pageSettings.terms.version && (
                    <span className="text-xs text-slate-500"> (v{pageSettings.terms.version})</span>
                  )}
                </span>
              </label>
            </div>
          )}

          <button
            onClick={submit}
            disabled={submitting}
            className="w-full px-4 py-3 rounded-lg font-semibold text-slate-950 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: primary }}
            data-testid="onb-submit"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> : null}
            Submit Application
          </button>
          <p className="text-xs text-slate-500 text-center">
            Your application will be reviewed within 1–3 business days.
          </p>
        </div>

        {/* T&C modal */}
        {termsOpen && pageSettings?.terms?.content && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setTermsOpen(false)}>
            <div
              className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="onb-terms-modal"
            >
              <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-100">Terms &amp; Conditions</h2>
                <button onClick={() => setTermsOpen(false)} className="text-slate-500 hover:text-slate-200">✕</button>
              </div>
              <div className="px-6 py-5 overflow-y-auto whitespace-pre-wrap text-sm text-slate-300 leading-relaxed">
                {pageSettings.terms.content}
              </div>
              <div className="px-6 py-3 border-t border-slate-800 text-right">
                <button
                  onClick={() => { setForm((f) => ({ ...f, terms_accepted: true })); setTermsOpen(false); }}
                  className="px-4 py-2 rounded font-semibold text-slate-950 text-sm"
                  style={{ background: primary }}
                  data-testid="onb-terms-accept"
                >
                  I agree
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
    <div className="flex items-center gap-2 mb-4 text-slate-200">
      {icon}
      <h2 className="text-sm font-semibold uppercase tracking-wide">{title}</h2>
    </div>
    {children}
  </div>
);

const Field = ({ label, children, testid }) => (
  <div className="space-y-1 mb-3 last:mb-0">
    <label className="text-xs text-slate-400 block">{label}</label>
    {React.cloneElement(children, { 'data-testid': testid || children.props['data-testid'] })}
  </div>
);

const FileUpload = ({ label, filled, onChange, testid }) => (
  <label className={`block mt-3 px-4 py-3 rounded-lg border border-dashed cursor-pointer transition-colors ${
    filled ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-slate-700 hover:border-slate-600 bg-slate-950/40'
  }`}>
    <div className="flex items-center gap-2 text-xs">
      {filled ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Upload className="w-4 h-4 text-slate-500" />}
      <span className={filled ? 'text-emerald-300' : 'text-slate-400'}>{filled ? 'File attached — click to replace' : label}</span>
    </div>
    <input type="file" accept="image/*,application/pdf" onChange={onChange} className="hidden" data-testid={testid} />
  </label>
);

const cls = () =>
  'w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 text-sm focus:outline-none focus:border-amber-400';

export default MarketplaceOnboardingPage;
